'use client';

/**
 * Shared synthesis-card body. Used by ObeyaHealthCard,
 * ObeyaQualityCard and RfqHealthCard — each renders an LLM synthesis
 * (summary + urgency + recommendations) returned by a focused BFF
 * strategy.
 *
 * One card → one BFF capability. Health calls `project_health`,
 * Quality `project_data_quality`, RFQ Health `rfq_health`. Each runs
 * its own inline_synthesis (HEALTH_SYNTHESIS / DATA_QUALITY template)
 * and returns the synthesis dict flat — `{summary, urgency,
 * recommendations, synthesis_metadata: {trust_level, ...}}`.
 *
 * Entity-agnostic: the variant resolves the entity (a project sid, an
 * RFQ sid) and passes `entitySid` + `entityParam`; this body never
 * reaches into a project-specific store.
 *
 * Replaces the legacy `useViewportFast/Slow("obeya", ...)` pipeline
 * the card inherited from when it lived in the legacy obeya widget.
 * Same payload shape, same render — just direct BFF call through
 * /ui/data, scope = caller's JWT (same path every other card on the
 * canvas uses).
 *
 * Pin-to-timeline still calls the `pin_synthesis` strategy unchanged.
 *
 * @see klyde_edge/ui_config/handlers/synthesis.py — project_health, project_data_quality
 */

import { useState, useMemo, useEffect } from 'react';
import { AlertTriangle, CheckCircle, Clock, Lightbulb, Loader2, Pin, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils/cn';
import { text, surfaceBase } from '@/lib/design/tokens.x';
import { useStrategyData } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useLanguageStore } from '@/lib/stores/language-store';
import { useTurn } from '@/lib/stores/turn-context';
import { useWorkspaceLabels } from '@/lib/stores/total-matrix-store';
import { api } from '@/lib/api/client';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';
import { useRailCardPreviewStore } from '@/lib/stores/rail-card-preview-store';
import type { HealthRecommendation, HealthSynthesis } from '@/lib/api/types';

interface SynthesisCardProps {
  title: string;
  /** BFF strategy this variant fetches from when mounted OUTSIDE the
   *  agentic transcript (mode1 sel:project, etc.). Inside the
   *  transcript the card defers to the agent's picked_capability
   *  on the turn — what the agent actually ran wins over what the
   *  card-variant default would have called. */
  strategy: string;
  /**
   * Strategy-name token the chatter post records as the source of
   * the synthesis (e.g. `'health_synthesis'`, `'data_quality'`).
   * Distinct from `strategy` because the pin contract uses
   * shorter names than the BFF strategy names.
   */
  pinStrategyName: string;
  /** The entity this synthesis is about — resolved by the card variant
   *  from the viewport (a project sid for ObeyaHealthCard, an RFQ sid
   *  for RfqHealthCard). `null` → the card shows `emptyLabel`. */
  entitySid: string | null;
  /** The BFF param name `strategy` expects the entity under —
   *  `'project_id'` for project_health, `'rfq_sid'` for rfq_health. */
  entityParam: string;
  /** Empty-state copy shown when no entity is selected. */
  emptyLabel: string;
  Icon: React.ComponentType<{ className?: string }>;
  iconColor: string;
}

interface StrategyResult {
  decide_results?: Record<string, unknown>;
  trust_level?: string;
}

interface PinResponse {
  success?: boolean;
  message_id?: number | string;
  error?: string;
}

interface SynthesisPayload {
  // Common across project_health + project_data_quality. Both return
  // {summary, urgency, recommendations, synthesis_metadata} at top
  // level; health adds rag_status, quality adds quality_score +
  // issues_found + remediation_steps. We type loosely + read defensively.
  summary?: string;
  urgency?: string;
  recommendations?: HealthRecommendation[];
  rag_status?: string;
  quality_score?: number;
  labels?: Record<string, string>;
  synthesis_metadata?: {
    conversation_id?: string;
    trust_level?: string;
    synthesis_method?: string;
    template?: string;
  };
}

interface StrategyEnvelope<T> {
  success: boolean;
  data: T;
  error?: string;
}

/** Shape-check: does this payload look like a SynthesisPayload? The
 *  renderer expects at least one of `summary`, `urgency`, or
 *  `recommendations` — the three load-bearing fields. Anything else
 *  is a payload from a capability that lied about its renderer (e.g.
 *  project_perf declaring renderer_card=obeya_health while returning
 *  a metrics shape). Self-suppress rather than render an empty card. */
function isSynthesisShape(p: unknown): p is SynthesisPayload {
  if (!p || typeof p !== 'object') return false;
  const o = p as Record<string, unknown>;
  return (
    typeof o.summary === 'string' ||
    typeof o.urgency === 'string' ||
    Array.isArray(o.recommendations)
  );
}


export function SynthesisCard({
  title, strategy, pinStrategyName, entitySid, entityParam, emptyLabel, Icon, iconColor,
}: SynthesisCardProps) {
  const queryClient = useQueryClient();
  const { contentKey } = useCardIdentity();

  // Per-turn context (transcript mount). When set, the agent has already
  // run the capability for this turn and the payload is frozen on the
  // turn. We render from that payload directly — no second fetch — and
  // the card stays pinned to this turn's project even when a later turn
  // shifts the selection store. Outside transcript (mode1 sel:project),
  // useTurn returns null and the card uses the `strategy` prop to fetch.
  const turn = useTurn();
  const turnPayload = turn?.response.payload as unknown;
  // Transcript turns carry the entity on `entity_sid`; outside the
  // transcript the variant passes `entitySid` from the viewport.
  const turnEntitySid = turn?.response.entity_sid ?? null;
  const entityId = turnEntitySid ?? entitySid;

  // Substrate-honest: when in transcript, defer to the agent's pick
  // (turn.response.picked_capability) rather than overriding it with
  // the card variant's default `strategy`. Outside transcript, use
  // the prop. Refresh button uses this name to invalidate cache.
  const effectiveStrategy = turn?.response.picked_capability ?? strategy;

  // Fetch only when no turn payload is available. In transcript mode
  // the turn carries the data; refetching would either duplicate the
  // dispatch or call the wrong strategy (e.g. project_health when the
  // agent ran project_perf).
  const fetchEnabled = !turnPayload && !!entityId;
  // Plumb `lang` from the global language toggle so the BFF receives
  // the viewer's locale (and downstream `inline_synthesis` calls Gemini
  // with `preferred_language=lang`). Without this every synthesis-card
  // surface — rfq_health, project_health, project_data_quality, etc.
  // — renders English regardless of the toggle. Same pattern every
  // other obeya/* card and the chat surface already use.
  const lang = useLanguageStore((s) => s.lang);
  // Closed-vocab display map for the urgency pill (low/medium/high
  // → 低/中/高). Fall-through lookup against the raw value. Source:
  // PR #1 backend / klyde_edge/ui_config/labels/workspace.py.
  const { urgency: urgencyLabels } = useWorkspaceLabels();
  const { data: envelope, isLoading, isFetching } = useStrategyData<SynthesisPayload>(
    strategy ?? null,
    { [entityParam]: entityId, ...(lang ? { lang } : {}) },
    fetchEnabled,
  );
  const isRefreshing = isFetching;
  const rawPayload: unknown =
    turnPayload ?? (envelope as unknown as StrategyEnvelope<SynthesisPayload> | undefined)?.data;

  // Scene export — register the synthesis card (health / quality / rfq_health)
  // into the capture envelope. Before the shape-check early return below
  // (Rules of Hooks). content_key from context (rail/canvas mount), fallback
  // to pinStrategyName; query_key matches the useStrategyData fetch above.
  useCardExport(useMemo(() => {
    const s = isSynthesisShape(rawPayload) ? rawPayload : undefined;
    return {
      content_key: contentKey ?? pinStrategyName,
      status: (s ? 'loaded' : entityId ? 'loading' : 'idle') as 'loaded' | 'loading' | 'idle',
      query_key: entityId
        ? ['strategy-data', strategy, { [entityParam]: entityId, ...(lang ? { lang } : {}) }]
        : null,
      text: s?.summary ? `${s.urgency ?? ''} · ${s.summary}` : title,
      json: {
        entity_sid: entityId ?? null,
        urgency: s?.urgency ?? null,
        summary: s?.summary ?? null,
        trust_level: s?.synthesis_metadata?.trust_level ?? null,
      },
    };
  }, [contentKey, pinStrategyName, rawPayload, entityId, strategy, entityParam, lang, title]));

  // Publish a compact one-line preview so a COLLAPSED CollapsibleCard
  // can still show live state (see rail-card-preview-store.ts) instead
  // of a bare label — this card only opts into staying mounted while
  // collapsed (CollapsibleCard's `alwaysMounted`) so this effect keeps
  // running and the preview stays current. Truncated free text, not a
  // parsed structured count: the summary is prose, and regexing a
  // number out of it would be fragile and English-specific.
  const setPreview = useRailCardPreviewStore((s) => s.setPreview);
  useEffect(() => {
    const previewKey = contentKey ?? pinStrategyName;
    if (!previewKey) return;
    const s = isSynthesisShape(rawPayload) ? rawPayload : undefined;
    if (!s?.summary) {
      setPreview(previewKey, null);
      return;
    }
    const urgencyText = urgencyLabels[s.urgency ?? ''] ?? s.urgency;
    const snippet = s.summary.length > 70 ? `${s.summary.slice(0, 70)}…` : s.summary;
    setPreview(previewKey, urgencyText ? `${urgencyText} · ${snippet}` : snippet);
  }, [contentKey, pinStrategyName, rawPayload, urgencyLabels, setPreview]);

  // Shape-check before rendering. If the payload doesn't carry the
  // synthesis shape, the card was mounted for a capability that
  // declared `renderer_card=obeya_health` but returns something else.
  // Self-suppress so the surrounding slot can fall through to a
  // graph fallback or the agent's narrative card alone.
  if (turn && rawPayload && !isSynthesisShape(rawPayload)) {
    return null;
  }
  const synthesis: SynthesisPayload | undefined = isSynthesisShape(rawPayload)
    ? rawPayload
    : undefined;
  const trustLevel = synthesis?.synthesis_metadata?.trust_level;
  // Card chrome (based_on/trust/pin/empty-state labels) rides the
  // SAME strategy payload as the synthesis itself — BFF-sourced
  // (_obeya_labels in klyde_edge/ui_config/handlers/obeya.py), not a
  // second fetch. The renderer paints what's shipped.
  const L = synthesis?.labels ?? {};

  const handleRefresh = () => {
    // Invalidate just this card's BFF. Canvas overview + rail Insights
    // mounts of the same variant share the query key (same strategy,
    // same entity) and refresh together via React Query.
    void queryClient.invalidateQueries({
      queryKey: ['strategy-data', effectiveStrategy],
    });
  };

  // Pin-to-timeline. Resolves the `('synthesis', 'pin')` BFF strategy
  // — the capability-store lookup gives us the live name; falls back
  // to the canonical literal if action= isn't filled on the backend
  // decoration (defensive — see the audit doc on `strategyName(...)!`
  // anti-patterns).
  const pinStrategy = useCapabilityStore((s) => s.strategyName('synthesis', 'pin'));
  const [isPinning, setIsPinning] = useState(false);
  const handlePin = async () => {
    if (!synthesis || !entityId) return;
    setIsPinning(true);
    try {
      const res = await api.getStrategyData<PinResponse>(
        pinStrategy ?? 'pin_synthesis',
        {
          entity_id: entityId,
          title,
          summary: synthesis.summary,
          urgency: synthesis.urgency,
          recommendations: synthesis.recommendations || [],
          strategy_name: pinStrategyName,
        },
      );
      if (res.success && res.data?.success) {
        toast.success(L.pinned ?? 'Pinned to timeline', {
          description: res.data.message_id
            ? `#${res.data.message_id} ${L.added_to_chatter ?? 'added to chatter'}`
            : (L.added_to_chatter ?? 'Added to chatter'),
        });
      } else {
        toast.error(L.pin_failed ?? 'Pin failed', {
          description: String(res.data?.error || res.error || 'Unknown error'),
        });
      }
    } catch (err) {
      toast.error(L.pin_failed ?? 'Pin failed', {
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setIsPinning(false);
    }
  };

  const urgencyIcon = (u?: string) => {
    if (u === 'low') return <CheckCircle className="h-4 w-4 text-green-400" />;
    if (u === 'high') return <AlertTriangle className="h-4 w-4 text-red-400" />;
    return <Clock className="h-4 w-4 text-amber-400" />;
  };

  const priorityColor = (p?: string) => {
    if (p === 'high') return 'bg-red-500/10 border-red-500/20 text-red-400';
    if (p === 'medium') return 'bg-amber-500/10 border-amber-500/20 text-amber-400';
    if (p === 'low') return 'bg-green-500/10 border-green-500/20 text-green-400';
    return 'bg-muted border-border';
  };

  return (
    <article className="rounded-md border border-border bg-card shadow-sm overflow-hidden h-full flex flex-col" style={{ fontSize: surfaceBase.rail }}>
      <header className="flex items-center gap-2 px-3 py-2 border-b border-border/60 shrink-0">
        <Icon className={cn('w-3.5 h-3.5', iconColor)} />
        <h3 className={text.heading}>{title}</h3>
        <div className="ml-auto flex items-center gap-0.5">
          <button
            onClick={handlePin}
            disabled={isPinning || !synthesis}
            className="p-1 rounded hover:bg-accent transition-colors disabled:opacity-50"
            title={L.pin_to_timeline ?? 'Pin to timeline'}
            aria-label={L.pin_to_timeline ?? 'Pin to timeline'}
          >
            <Pin
              className={cn(
                'w-3.5 h-3.5 text-muted-foreground',
                isPinning && 'animate-pulse',
              )}
            />
          </button>
          {/* Refresh is suppressed when the card is bound to a turn —
              the payload is a frozen snapshot of what the agent ran
              for that turn. To "refresh", the user asks a new turn. */}
          {!turn && (
            <button
              onClick={handleRefresh}
              disabled={isRefreshing || !entityId}
              className="p-1 rounded hover:bg-accent transition-colors disabled:opacity-50"
              title="Refresh synthesis"
              aria-label="Refresh synthesis"
            >
              <RefreshCw
                className={cn(
                  'w-3.5 h-3.5 text-muted-foreground',
                  isRefreshing && 'animate-spin',
                )}
              />
            </button>
          )}
        </div>
      </header>
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {!entityId ? (
          <div className={text.muted}>{emptyLabel}</div>
        ) : isLoading && !synthesis ? (
          <div className={cn(text.muted, 'flex items-center gap-2')}>
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {L.generating ?? 'Generating…'}
          </div>
        ) : !synthesis ? (
          <div className={cn(text.muted, 'italic')}>{L.no_synthesis ?? 'No synthesis available'}</div>
        ) : (
          <>
            {/* Urgency card — matches the legacy obeya widget's colored
                container so the visual weight reads the same on both
                modes. */}
            <div
              className={cn(
                'flex items-start gap-2 p-2 rounded-md border',
                synthesis.urgency === 'low' && 'bg-green-500/10 border-green-500/20',
                synthesis.urgency === 'medium' && 'bg-amber-500/10 border-amber-500/20',
                synthesis.urgency === 'high' && 'bg-red-500/10 border-red-500/20',
              )}
            >
              {urgencyIcon(synthesis.urgency)}
              <div className="min-w-0 flex-1">
                <p className={cn(text.title, 'capitalize')}>
                  {(urgencyLabels[synthesis.urgency ?? ''] ?? synthesis.urgency)} {L.urgency ?? 'Urgency'}
                </p>
                <p className={cn(text.body, 'mt-0.5 leading-relaxed')}>
                  {synthesis.summary}
                </p>
              </div>
            </div>
            {/* Recommendations — `action` + `based_on` are the canonical
                response field names (see `HealthRecommendation` in
                `lib/api/types.ts`). The previous version of this card
                read `description` / `rationale` and silently rendered
                empty bodies because those fields don't exist in the
                response. */}
            {synthesis.recommendations && synthesis.recommendations.length > 0 && (
              <div className="space-y-1.5">
                {synthesis.recommendations.map((rec: HealthRecommendation, i: number) => (
                  <div
                    key={i}
                    className={cn(
                      'rounded-md border px-2 py-1.5',
                      priorityColor(rec.priority),
                    )}
                  >
                    <div className="flex items-start gap-1.5">
                      <Lightbulb className="h-3 w-3 shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className={cn(text.body, 'leading-snug')}>{rec.action}</p>
                      </div>
                      {rec.priority && (
                        <span className={cn(text.muted, 'px-1.5 py-0.5 rounded bg-background/50 capitalize shrink-0')}>
                          {rec.priority}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {trustLevel && (
              <div className={cn(text.muted, 'pt-1 border-t border-border/40')}>
                {L.trust ?? 'Trust:'} {trustLevel}
              </div>
            )}
          </>
        )}
      </div>
    </article>
  );
}
